import {Component} from '@angular/core';
import {CommonModule} from '@angular/common';
import {filter, switchMap, take, tap} from 'rxjs/operators';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatButtonModule} from '@angular/material/button';
import {TranslateModule} from '@ngx-translate/core';
import {
  NavHistoryObject,
  NavigationHistoryService
} from '../../../../core/services/navigation-history.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../../shared/models/diagram-version.constant';
import {EditorRegistryService} from '../../../../diagram-render/mx-graph/editor/editor-registry.service';
import {DiagramJsonHandlerService} from '../../../../diagram-data/diagram-json-handler.service';
import {NavigationService} from '../../../../core/navigation/navigation.service';
import {DrilldownService} from '../../../../diagram-interaction/services/drilldown.service';
import {VersionsService} from '../../../../core/services/versions.service';
import {DiagramStatePipe} from '../../../shared/pipes/diagram-state.pipe';
import {IconButtonComponent} from '@q9elements/ui-kit/components';
import {Q9HtmlDecoderPipe, Q9TrackDirective} from '@q9elements/ui-kit/common';
import {ScreenshotPopoverDirective} from 'src/app/editor-interface/shared/directives/screenshot-popover.directive';

@Component({
  selector: 'app-diagram-navigation',
  standalone: true,
  imports: [
    CommonModule,
    DiagramStatePipe,
    IconButtonComponent,
    MatMenuModule,
    MatTooltipModule,
    Q9TrackDirective,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    Q9HtmlDecoderPipe,
    ScreenshotPopoverDirective
  ],
  templateUrl: './diagram-navigation.component.html',
  styleUrls: ['./diagram-navigation.component.scss']
})
export class DiagramNavigationComponent {
  navHistoryList: NavHistoryObject[];

  constructor(
    public diagramJsonSvc: DiagramJsonHandlerService,
    public navSvc: NavigationService,
    private registry: EditorRegistryService,
    private drilldownSvc: DrilldownService,
    private versionsSvc: VersionsService,
    private navigationHistoryService: NavigationHistoryService
  ) {}

  get navigationTopDisabled() {
    const json = this.diagramJsonSvc.get();
    const customTop = this.navSvc.getCustomTop();

    if (customTop) {
      return this.navSvc.isNavActive() || json.diagramId === customTop;
    }

    const topDiagramId = json.topDiagramId;

    return !(json.diagramId && topDiagramId) || this.navSvc.isNavActive();
  }

  get navigationParentDisabled() {
    const json = this.diagramJsonSvc.get();
    const customTop = this.navSvc.getCustomTop();

    if (customTop) {
      return this.navSvc.isNavActive() || json.diagramId === customTop || !json.parentDiagramId;
    }

    return !(json.diagramId && json.parentDiagramId) || this.navSvc.isNavActive();
  }

  get navigationPreviousDisabled() {
    return !this.navSvc.getPrevious() || this.navSvc.isNavActive();
  }

  isTooltipDisabled(navItem: NavHistoryObject): boolean {
    const navItemNameLength = navItem?.name?.length;

    return navItemNameLength ? navItemNameLength < 33 : true;
  }

  goToParent() {
    this.diagramJsonSvc.diagramJson$
      .pipe(
        take(1),
        switchMap(json => {
          const graph = this.registry.getGraph();

          if (graph) {
            graph.stopEditing(false);
          }

          return this.navSvc.relative(json.parentDiagramId).then(() => json);
        }),
        switchMap(json =>
          this.diagramJsonSvc.diagramJson$.pipe(
            take(2),
            filter(newJson => newJson.diagramId !== json.diagramId),
            tap(() => setTimeout(() => this.drilldownSvc.highlight(json.diagramId, true), 100))
          )
        ),
        tap(() => {
          this.navHistoryList = this.navigationHistoryService.getNavHistory();
          this.versionsSvc.getMapAuthorisationMode();
        })
      )
      .subscribe();
  }

  onHistoryMenu() {
    this.navHistoryList = this.navigationHistoryService.getNavHistory();
  }

  goTop() {
    this.diagramJsonSvc.diagramJson$
      .pipe(
        take(1),
        switchMap(json => {
          const graph = this.registry.getGraph();

          if (graph) {
            graph.stopEditing(false);
          }

          return this.navSvc.navigateToTopDiagram(json.topDiagramId).then(() => json);
        }),
        switchMap(json =>
          this.diagramJsonSvc.diagramJson$.pipe(
            take(2),
            filter(newJson => newJson.diagramId !== json.diagramId),
            tap(() => setTimeout(() => this.drilldownSvc.highlight(json.diagramId, true), 100))
          )
        ),
        tap(() => {
          this.navigationHistoryService.getNavHistory();
          this.versionsSvc.getMapAuthorisationMode();
        })
      )
      .subscribe();
  }

  navigateToPrevious() {
    this.diagramJsonSvc.diagramJson$
      .pipe(
        take(1),
        tap(() => {
          const graph = this.registry.getGraph();

          if (graph) {
            graph.stopEditing(false);
          }
        }),
        switchMap(json => this.navSvc.previous().then(() => json)),
        switchMap(json =>
          this.diagramJsonSvc.diagramJson$.pipe(
            take(2),
            tap(diagramJson => this.versionsSvc.setStartReleaseVersionName(diagramJson)),
            filter(newJson => newJson.diagramId !== json.diagramId),
            tap(() => setTimeout(() => this.drilldownSvc.highlight(json.diagramId, true), 100))
          )
        ),
        tap(() => {
          this.versionsSvc.getMapAuthorisationMode();
          this.navigationHistoryService.getNavHistory();
        })
      )
      .subscribe();
  }

  navigateToNavHistoryItem(index) {
    this.diagramJsonSvc.diagramJson$
      .pipe(
        take(1),
        switchMap(json => this.navSvc.selected(index).then(() => json)),
        switchMap(json =>
          this.diagramJsonSvc.diagramJson$.pipe(
            take(2),
            tap(diagramJson => this.versionsSvc.setStartReleaseVersionName(diagramJson)),
            filter(newJson => newJson.diagramId !== json.diagramId),
            tap(() => setTimeout(() => this.drilldownSvc.highlight(json.diagramId, true), 100))
          )
        ),
        tap(() => {
          this.navigationHistoryService.getNavHistory();
          this.versionsSvc.getMapAuthorisationMode();
        })
      )
      .subscribe();
  }

  screenConfig(type) {
    let id;
    let state;

    switch (type) {
      case 'parent':
        id = this.diagramJsonSvc.get().parentDiagramId;
        break;

      case 'top': {
        const json = this.diagramJsonSvc.get();
        const topDiagramId = this.navSvc.getCustomTop() || json.topDiagramId;

        id = topDiagramId;
        break;
      }

      case 'previous':
        id = this.navSvc.getPrevious() ? this.navSvc.getPrevious().id : null;
        state = this.navSvc.getPrevious()
          ? this.navSvc.getPrevious().state
          : DIAGRAM_VERSION_STATES_ENUM.DRAFT;
        break;
    }

    return {
      id: id,
      state: state || this.navSvc.getCurrent().state,
      size: '_300x200'
    };
  }
}
